<script setup lang="ts">
import axios, { isAxiosError } from 'axios'
import { VForm } from 'vuetify/components/VForm'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import type { App } from '@/types/generated'
import { Modules } from '@/types/generated'

type CatalogItem = App.Http.Controllers.LoopAvatar.LoopAvatarCatalogItemData

interface OrganizationOption {
  id: string
  title: string
}

definePage({
  meta: {
    action: 'manage',
    subject: 'all',
  },
})

const { t } = useI18n()

const MAX_PACKAGE_BYTES = 100 * 1024 * 1024

// VSelect treats a null model as "nothing selected", so shared visibility needs its own value.
const ALL_ORGANIZATIONS = 'all'

const { data: avatarsData, execute: fetchAvatars, isFetching } = await useApi<CatalogItem[]>('/loop-avatars')
const { data: organizationsData } = await useApi<OrganizationOption[]>('/organizations/all')

const organizations = computed<OrganizationOption[]>(() => organizationsData.value ?? [])

const organizationTitle = (organizationId: string | null): string => {
  if (organizationId === null)
    return t('loopAvatars.visibility.all')

  return organizations.value.find(organization => organization.id === organizationId)?.title ?? organizationId
}

const rows = computed(() => (avatarsData.value ?? []).map(avatar => ({
  ...avatar,
  visibilityTitle: organizationTitle(avatar.organizationId),
})))

const searchQuery = ref('')

const headers = [
  { title: t('loopAvatars.headers.poster'), key: 'posterUrl', sortable: false },
  { title: t('loopAvatars.headers.name'), key: 'name' },
  { title: t('loopAvatars.headers.gender'), key: 'gender' },
  { title: t('loopAvatars.headers.emotion'), key: 'emotion' },
  { title: t('loopAvatars.headers.visibility'), key: 'visibilityTitle' },
  { title: t('loopAvatars.headers.version'), key: 'version' },
  { title: t('loopAvatars.headers.profiles'), key: 'profilesCount' },
  { title: '', key: 'actions', sortable: false },
]

const genderOptions = Object.values(Modules.LoopAvatar.Enums.LoopAvatarGenderEnum)
  .map(gender => ({ value: gender, title: t(`loopAvatars.gender.${gender}`) }))

const emotionOptions = Object.values(Modules.LoopAvatar.Enums.LoopAvatarEmotionEnum)
  .map(emotion => ({ value: emotion, title: t(`loopAvatars.emotion.${emotion}`) }))

const visibilityOptions = computed(() => [
  { value: ALL_ORGANIZATIONS, title: t('loopAvatars.visibility.all') },
  ...organizations.value.map(organization => ({ value: organization.id, title: organization.title })),
])

const toOrganizationId = (visibility: string): string | null => visibility === ALL_ORGANIZATIONS ? null : visibility

const toVisibility = (organizationId: string | null): string => organizationId ?? ALL_ORGANIZATIONS

const requestFailedMessage = () => t('loopAvatars.errors.requestFailed')

const validationMessages = (status: number | undefined, errors: unknown): string[] => {
  if (status === 422 && errors && typeof errors === 'object')
    return Object.values(errors).flat().map(String)

  return [requestFailedMessage()]
}

const packageRules = [
  requiredValidator,
  (files: File[]) => !files?.length || files[0].size <= MAX_PACKAGE_BYTES || t('loopAvatars.validation.packageTooLarge'),
]

const isUploadDialogVisible = ref(false)
const versionTarget = ref<CatalogItem | null>(null)
const uploadForm = ref<VForm>()
const uploadFiles = ref<File[]>([])
const uploadVisibility = ref<string>(ALL_ORGANIZATIONS)
const uploadErrors = ref<string[]>([])
const isUploading = ref(false)

const openUploadDialog = (target: CatalogItem | null) => {
  versionTarget.value = target
  uploadFiles.value = []
  uploadVisibility.value = ALL_ORGANIZATIONS
  uploadErrors.value = []
  isUploadDialogVisible.value = true
}

const submitUpload = async () => {
  const { valid } = await uploadForm.value!.validate()
  if (!valid)
    return

  const formData = new FormData()

  formData.append('package', uploadFiles.value[0])

  const organizationId = toOrganizationId(uploadVisibility.value)
  if (versionTarget.value === null && organizationId !== null)
    formData.append('organizationId', organizationId)

  const url = versionTarget.value === null
    ? '/api/loop-avatars'
    : `/api/loop-avatars/${versionTarget.value.id}/versions`

  isUploading.value = true
  uploadErrors.value = []
  try {
    await axios.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    isUploadDialogVisible.value = false
    await fetchAvatars()
  }
  catch (error) {
    const response = isAxiosError(error) ? error.response : undefined

    uploadErrors.value = validationMessages(response?.status, response?.data?.errors)
  }
  finally {
    isUploading.value = false
  }
}

const isEditDialogVisible = ref(false)
const isDetachConfirmVisible = ref(false)
const editTarget = ref<CatalogItem | null>(null)
const editForm = ref<VForm>()
const editErrors = ref<string[]>([])
const isSaving = ref(false)
const detachProfilesCount = ref(0)

const editData = ref({
  name: '',
  gender: Modules.LoopAvatar.Enums.LoopAvatarGenderEnum.Male,
  emotion: Modules.LoopAvatar.Enums.LoopAvatarEmotionEnum.Calm,
  visibility: ALL_ORGANIZATIONS,
})

const openEditDialog = (avatar: CatalogItem) => {
  editTarget.value = avatar
  editData.value = {
    name: avatar.name,
    gender: avatar.gender,
    emotion: avatar.emotion,
    visibility: toVisibility(avatar.organizationId),
  }
  editErrors.value = []
  isEditDialogVisible.value = true
}

const saveEdit = async () => {
  const avatar = editTarget.value
  if (avatar === null)
    return

  isSaving.value = true
  editErrors.value = []
  try {
    await $api(`/loop-avatars/${avatar.id}`, {
      method: 'PUT',
      body: {
        name: editData.value.name,
        gender: editData.value.gender,
        emotion: editData.value.emotion,
        organizationId: toOrganizationId(editData.value.visibility),
      },
    })
    isEditDialogVisible.value = false
    await fetchAvatars()
  }
  catch (error: any) {
    editErrors.value = validationMessages(error?.statusCode, error?.data?.errors)
  }
  finally {
    isSaving.value = false
  }
}

const submitEdit = async () => {
  const avatar = editTarget.value
  if (avatar === null)
    return

  const { valid } = await editForm.value!.validate()
  if (!valid)
    return

  const newOrganizationId = toOrganizationId(editData.value.visibility)
  const narrowsVisibility = newOrganizationId !== null && newOrganizationId !== avatar.organizationId

  if (!narrowsVisibility) {
    await saveEdit()

    return
  }

  isSaving.value = true
  editErrors.value = []
  try {
    const preview = await $api<{ profiles: number }>(`/loop-avatars/${avatar.id}/detach-preview`, {
      query: { organizationId: newOrganizationId },
    })

    detachProfilesCount.value = preview.profiles
  }
  catch (error: any) {
    editErrors.value = validationMessages(error?.statusCode, error?.data?.errors)

    return
  }
  finally {
    isSaving.value = false
  }

  if (detachProfilesCount.value > 0)
    isDetachConfirmVisible.value = true
  else
    await saveEdit()
}

const isDeleteConfirmVisible = ref(false)
const deleteTarget = ref<CatalogItem | null>(null)
const pageError = ref('')
const isPageErrorVisible = ref(false)

const deleteQuestion = computed(() => {
  const avatar = deleteTarget.value
  if (avatar === null)
    return ''

  return avatar.profilesCount > 0
    ? t('loopAvatars.confirmDeleteWithProfiles', { name: avatar.name, count: avatar.profilesCount })
    : t('loopAvatars.confirmDelete', { name: avatar.name })
})

const openDeleteConfirm = (avatar: CatalogItem) => {
  deleteTarget.value = avatar
  isDeleteConfirmVisible.value = true
}

const deleteAvatar = async () => {
  const avatar = deleteTarget.value
  if (avatar === null)
    return

  try {
    await $api(`/loop-avatars/${avatar.id}`, { method: 'DELETE' })
  }
  catch {
    pageError.value = requestFailedMessage()
    isPageErrorVisible.value = true
  }
  await fetchAvatars()
}
</script>

<template>
  <div>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <VTextField
            v-model="searchQuery"
            clearable
            :placeholder="t('search')"
            style=" min-inline-size: 200px;"
          />
        </div>
        <VSpacer />
        <VBtn
          prepend-icon="tabler-upload"
          @click="openUploadDialog(null)"
        >
          {{ t('loopAvatars.upload') }}
        </VBtn>
      </VCardText>

      <VDataTable
        :headers="headers"
        :items="rows"
        :search="searchQuery ?? ''"
        :loading="isFetching"
        :loading-text="t('loading')"
        :no-data-text="t('loopAvatars.empty')"
        :items-per-page="-1"
        item-value="id"
        class="text-no-wrap"
      >
        <template #item.posterUrl="{ item }">
          <VImg
            :src="item.posterUrl"
            width="48"
            height="64"
            cover
            class="rounded my-2"
          />
        </template>
        <template #item.gender="{ item }">
          {{ t(`loopAvatars.gender.${item.gender}`) }}
        </template>
        <template #item.emotion="{ item }">
          {{ t(`loopAvatars.emotion.${item.emotion}`) }}
        </template>
        <template #item.actions="{ item }">
          <div class="d-flex justify-end">
            <IconBtn
              :title="t('loopAvatars.newVersion')"
              @click="openUploadDialog(item)"
            >
              <VIcon icon="tabler-upload" />
            </IconBtn>
            <IconBtn
              :title="t('loopAvatars.edit')"
              @click="openEditDialog(item)"
            >
              <VIcon icon="tabler-edit" />
            </IconBtn>
            <IconBtn
              :title="t('loopAvatars.delete')"
              @click="openDeleteConfirm(item)"
            >
              <VIcon icon="tabler-trash" />
            </IconBtn>
          </div>
        </template>
        <template #bottom />
      </VDataTable>
    </VCard>

    <VDialog
      v-model="isUploadDialogVisible"
      :width="$vuetify.display.smAndDown ? 'auto' : 580"
      :persistent="isUploading"
    >
      <DialogCloseBtn
        :disabled="isUploading"
        @click="isUploadDialogVisible = false"
      />

      <VCard class="pa-5 pa-sm-8">
        <VCardItem class="text-center">
          <VCardTitle class="text-h5 mb-3">
            {{ versionTarget ? t('loopAvatars.newVersion') : t('loopAvatars.upload') }}
          </VCardTitle>
          <VCardSubtitle v-if="versionTarget">
            {{ t('loopAvatars.newVersionHint', { name: versionTarget.name, version: versionTarget.version }) }}
          </VCardSubtitle>
        </VCardItem>

        <VCardText>
          <VForm
            ref="uploadForm"
            :disabled="isUploading"
            @submit.prevent="submitUpload"
          >
            <VRow>
              <VCol cols="12">
                <VFileInput
                  v-model="uploadFiles"
                  :rules="packageRules"
                  accept=".zip,application/zip,application/x-zip-compressed"
                  :label="t('loopAvatars.fields.package')"
                  :hint="t('loopAvatars.packageHint')"
                  persistent-hint
                  prepend-icon="tabler-file-type-zip"
                  @update:model-value="uploadErrors = []"
                />
              </VCol>
              <VCol
                v-if="!versionTarget"
                cols="12"
              >
                <AppSelect
                  v-model="uploadVisibility"
                  :items="visibilityOptions"
                  :label="t('loopAvatars.headers.visibility')"
                />
              </VCol>
              <VCol
                v-if="uploadErrors.length"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                >
                  <div
                    v-for="(message, index) in uploadErrors"
                    :key="index"
                  >
                    {{ message }}
                  </div>
                </VAlert>
              </VCol>
              <VCol
                cols="12"
                class="d-flex flex-wrap justify-center gap-4"
              >
                <VBtn
                  type="submit"
                  :loading="isUploading"
                  :disabled="isUploading"
                >
                  {{ t('save') }}
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  :disabled="isUploading"
                  @click="isUploadDialogVisible = false"
                >
                  {{ t('cancel') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <VDialog
      v-model="isEditDialogVisible"
      :width="$vuetify.display.smAndDown ? 'auto' : 580"
    >
      <DialogCloseBtn @click="isEditDialogVisible = false" />

      <VCard class="pa-5 pa-sm-8">
        <VCardItem class="text-center">
          <VCardTitle class="text-h5 mb-3">
            {{ t('loopAvatars.edit') }}
          </VCardTitle>
        </VCardItem>

        <VCardText>
          <VForm
            ref="editForm"
            :disabled="isSaving"
            @submit.prevent="submitEdit"
          >
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="editData.name"
                  :label="t('loopAvatars.headers.name')"
                  :rules="[requiredValidator]"
                  maxlength="255"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <AppSelect
                  v-model="editData.gender"
                  :items="genderOptions"
                  :label="t('loopAvatars.headers.gender')"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <AppSelect
                  v-model="editData.emotion"
                  :items="emotionOptions"
                  :label="t('loopAvatars.headers.emotion')"
                />
              </VCol>
              <VCol cols="12">
                <AppSelect
                  v-model="editData.visibility"
                  :items="visibilityOptions"
                  :label="t('loopAvatars.headers.visibility')"
                />
              </VCol>
              <VCol
                v-if="editErrors.length"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                >
                  <div
                    v-for="(message, index) in editErrors"
                    :key="index"
                  >
                    {{ message }}
                  </div>
                </VAlert>
              </VCol>
              <VCol
                cols="12"
                class="d-flex flex-wrap justify-center gap-4"
              >
                <VBtn
                  type="submit"
                  :loading="isSaving"
                  :disabled="isSaving"
                >
                  {{ t('save') }}
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  @click="isEditDialogVisible = false"
                >
                  {{ t('cancel') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <ConfirmDialog
      v-model:isDialogVisible="isDetachConfirmVisible"
      :confirmation-question="t('loopAvatars.confirmDetach', { count: detachProfilesCount })"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="saveEdit"
    />

    <ConfirmDialog
      v-model:isDialogVisible="isDeleteConfirmVisible"
      :confirmation-question="deleteQuestion"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="deleteAvatar"
    />

    <VSnackbar
      v-model="isPageErrorVisible"
      color="error"
    >
      {{ pageError }}
    </VSnackbar>
  </div>
</template>
